import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Package, Brain, Shield } from "lucide-react";

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-background" />
        <div className="relative max-w-6xl mx-auto px-4 py-16 sm:py-24">
          <div className="text-center">
            <Badge variant="secondary" className="mb-4">
              Invite Only
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">
              Starling
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              AI-powered delivery verification and QA platform. Ensure every delivery 
              meets quality standards with intelligent photo analysis.
            </p>
            <a href="/api/login">
              <Button size="lg" data-testid="button-login">
                Sign In to Continue
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-2xl font-bold mb-2">Platform Features</h2>
          <p className="text-muted-foreground">Streamline your delivery QA process</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <Card className="hover-elevate">
            <CardHeader>
              <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center mb-2">
                <CheckCircle className="w-5 h-5 text-primary" />
              </div>
              <CardTitle className="text-lg">Delivery QA</CardTitle>
              <CardDescription>
                Review and verify completed deliveries with a streamlined workflow
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>Address verification</li>
                <li>Drop zone confirmation</li>
                <li>Photo-based review</li>
              </ul>
            </CardContent>
          </Card>

          <Card className="hover-elevate">
            <CardHeader>
              <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center mb-2">
                <Brain className="w-5 h-5 text-primary" />
              </div>
              <CardTitle className="text-lg">AI Training</CardTitle>
              <CardDescription>
                Train AI to recognize correct deliveries from verified photos
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>Learn from verified deliveries</li>
                <li>Auto-predict correctness</li>
                <li>Continuous improvement</li>
              </ul>
            </CardContent>
          </Card>

          <Card className="hover-elevate">
            <CardHeader>
              <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center mb-2">
                <Package className="w-5 h-5 text-primary" />
              </div>
              <CardTitle className="text-lg">Onfleet Integration</CardTitle>
              <CardDescription>
                Seamlessly sync tasks from Onfleet for real-time QA
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>Automatic task sync</li>
                <li>Webhook support</li>
                <li>EST timezone normalization</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Access Section */}
      <div className="bg-muted/30 border-t">
        <div className="max-w-6xl mx-auto px-4 py-16 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-primary" />
            <span className="font-medium">Invite-Only Access</span>
          </div>
          <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
            This platform is available to approved team members only. 
            Contact your administrator to request access.
          </p>
          <a href="/api/login">
            <Button variant="outline" data-testid="button-login-secondary">
              Sign In
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
}
